import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { cmsTextClass, primaryButtonClass, sheetClass } from "../../styles";

/**
 * Site1 header: the business's logo (or its name, set as text), the section
 * links, and the booking button.
 *
 * Solid white, not a translucent blur: Chromium paints a backdrop-filter
 * layer past the rounded corners of the iframe the dashboard previews this
 * page in.
 *
 * The links collapse into a drawer below `lg` (1024px). The booking button
 * stays in the bar from `sm` up; on a phone it moves into the drawer, because
 * a long label beside the name and the menu toggle leaves the name no room —
 * unless there is no drawer at all, in which case it stays.
 */

const navLinkClass =
  "block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-600 transition-colors hover:text-[var(--site1-primary-strong)] focus-visible:text-[var(--site1-primary-strong)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong)] motion-reduce:transition-none";

const Header = ({
  logo = null,
  businessName = null,
  menuItems = [],
  ctaText = null,
  onCta = null,
  onNavigate = null,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleRef = useRef(null);
  const hasMenu = menuItems.length > 0;

  // Close on Escape and hand focus back to the toggle, so a keyboard user is
  // not left tabbing through a drawer that is no longer on screen.
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        toggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  // The drawer is fixed and covers the viewport; letting the page scroll behind
  // it is the classic mobile-menu bug.
  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);
    onNavigate?.(href);
  };

  const handleCta = () => {
    setIsMenuOpen(false);
    onCta?.();
  };

  const renderNavItems = (linkClass, tabbable = true) =>
    menuItems.map((item) => (
      <li key={item.href}>
        <a
          className={linkClass}
          href={item.href}
          onClick={(e) => handleNavClick(e, item.href)}
          tabIndex={tabbable ? 0 : -1}
        >
          {item.label}
        </a>
      </li>
    ));

  return (
    <header className="sticky top-0 z-40 border-b border-ink-100 bg-white">
      <div className={`${sheetClass} flex min-h-16 items-center gap-3 md:min-h-18`}>
        <a
          href="/"
          className="mr-auto flex min-w-0 items-center rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--site1-primary-strong)]"
          onClick={(e) => handleNavClick(e, "/")}
          aria-label={businessName || t("BookingPage.menu.home")}
        >
          {/* Orgs without an uploaded logo get their name set as text rather
              than a shared stock mark. */}
          {logo ? (
            <img src={logo} className="max-h-10 w-auto max-w-[11rem] object-contain md:max-h-11" alt={businessName || ""} />
          ) : (
            <span className={`${cmsTextClass} line-clamp-2 text-lg leading-tight font-bold text-[var(--site1-heading)] md:text-xl`}>
              {businessName}
            </span>
          )}
        </a>

        {hasMenu && (
          <nav className="hidden lg:block" aria-label={t("Common.mainNavigation")}>
            <ul className="m-0 flex list-none gap-1 p-0">{renderNavItems(navLinkClass)}</ul>
          </nav>
        )}

        {/* The wrapper carries the breakpoint: `hidden` appended to the
            button's own `inline-flex` would lose or win by stylesheet order. */}
        {ctaText && (
          <div className={hasMenu ? "hidden shrink-0 sm:block" : "shrink-0"}>
            <button type="button" onClick={handleCta} className={primaryButtonClass}>
              {ctaText}
            </button>
          </div>
        )}

        {hasMenu && (
          <button
            ref={toggleRef}
            type="button"
            className="-mr-2 flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-900 transition-colors hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-[var(--site1-primary-strong)] lg:hidden"
            aria-expanded={isMenuOpen}
            aria-controls="site1-mobile-nav"
            aria-label={isMenuOpen ? t("Common.closeMenu") : t("Common.openMenu")}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <i className={`bi ${isMenuOpen ? "bi-x-lg" : "bi-list"} text-2xl`} aria-hidden="true" />
          </button>
        )}
      </div>

      {hasMenu && (
        <>
          <div
            className={`fixed inset-0 z-50 bg-ink-900/50 transition-opacity motion-reduce:transition-none lg:hidden ${
              isMenuOpen ? "visible opacity-100" : "invisible opacity-0"
            }`}
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />

          <nav
            id="site1-mobile-nav"
            /* Off-canvas by transform rather than a negative offset, so the
               drawer cannot widen the document while closed. h-dvh keeps the
               last item clear of the iOS Safari toolbar. */
            className={`fixed top-0 right-0 z-50 flex h-dvh w-[min(20rem,85vw)] flex-col overflow-y-auto bg-white px-5 pt-5 pb-6 shadow-card-hover transition-transform motion-reduce:transition-none lg:hidden ${
              isMenuOpen ? "visible translate-x-0" : "invisible translate-x-full"
            }`}
            aria-label={t("Common.mainNavigation")}
            aria-hidden={!isMenuOpen}
          >
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                className="flex size-11 cursor-pointer items-center justify-center rounded-lg text-ink-700 hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-[var(--site1-primary-strong)]"
                aria-label={t("Common.closeMenu")}
                tabIndex={isMenuOpen ? 0 : -1}
                onClick={() => {
                  setIsMenuOpen(false);
                  toggleRef.current?.focus();
                }}
              >
                <i className="bi bi-x-lg text-xl" aria-hidden="true" />
              </button>
            </div>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {renderNavItems(`${navLinkClass} text-base`, isMenuOpen)}
            </ul>
            {ctaText && (
              <button
                type="button"
                onClick={handleCta}
                tabIndex={isMenuOpen ? 0 : -1}
                className={`${primaryButtonClass} mt-6 w-full`}
              >
                {ctaText}
              </button>
            )}
          </nav>
        </>
      )}
    </header>
  );
};

Header.propTypes = {
  logo: PropTypes.string,
  businessName: PropTypes.string,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })
  ),
  ctaText: PropTypes.string,
  onCta: PropTypes.func,
  onNavigate: PropTypes.func,
};

export default Header;
